import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import { FiPlus, FiMoreVertical, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { useHabits, Habit } from '../../contexts/HabitContext';
import HabitForm from './components/HabitForm';
import HabitTracker from './components/HabitTracker';

const HabitsPage: React.FC = () => {
  const {
    habits,
    isLoading,
    fetchHabits,
    deleteHabit,
    createHabit,
    updateHabit,
  } = useHabits();
  const [selectedHabit, setSelectedHabit] = useState<Habit | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchHabits();
  }, [fetchHabits]);

  const handleCreateHabit = async (habitData: Omit<Habit, 'id' | 'created_at'>) => {
    await createHabit(habitData);
    onClose();
  };

  const handleUpdateHabit = async (habitData: Omit<Habit, 'id' | 'created_at'>) => {
    if (selectedHabit) {
      await updateHabit(selectedHabit.id, habitData);
      setSelectedHabit(null);
      onClose();
    }
  };

  const handleDeleteHabit = async (habitId: string) => {
    await deleteHabit(habitId);
  };

  const handleEditClick = (habit: Habit) => {
    setSelectedHabit(habit);
    onOpen();
  };

  const handleAddClick = () => {
    setSelectedHabit(null);
    onOpen();
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Habits</Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={handleAddClick}
        >
          Add Habit
        </Button>
      </HStack>

      {habits.length === 0 ? (
        <Box
          p={8}
          textAlign="center"
          bg={bgColor}
          borderRadius="lg"
          border="1px dashed"
          borderColor={borderColor}
        >
          <Text color="gray.500" mb={4}>
            No habits created yet
          </Text>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleAddClick}
          >
            Create Your First Habit
          </Button>
        </Box>
      ) : (
        <VStack spacing={6} align="stretch">
          {habits.map((habit) => (
            <Box
              key={habit.id}
              bg={bgColor}
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
              overflow="hidden"
            >
              <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
                <HStack justify="space-between">
                  <VStack align="start" spacing={1}>
                    <Heading size="md">{habit.name}</Heading>
                    {habit.description && (
                      <Text color="gray.500" fontSize="sm">
                        {habit.description}
                      </Text>
                    )}
                  </VStack>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<FiMoreVertical />}
                      variant="ghost"
                      aria-label="Options"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<FiEdit2 />}
                        onClick={() => handleEditClick(habit)}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        icon={<FiTrash2 />}
                        onClick={() => handleDeleteHabit(habit.id)}
                        color="red.500"
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </Box>
              <Box p={4}>
                <HabitTracker habit={habit} />
              </Box>
            </Box>
          ))}
        </VStack>
      )}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedHabit ? 'Edit Habit' : 'Create New Habit'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <HabitForm
              initialData={selectedHabit || undefined}
              onSubmit={selectedHabit ? handleUpdateHabit : handleCreateHabit}
              onCancel={onClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default HabitsPage; 